// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xSaveShipping from "@bg/App/Handlers/OnePageCheckout/XngSaveShipping";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { CHECKOUT_BILLING_FORM_ID } from "@bg/Overrides/Widgets/OnePageCheckout/OnePageCheckoutShippingSection";
import { doScroll } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import setPaymentDetailsState from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetPaymentDetailsState";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { OnePageCheckoutFormContext } from "@insite/content-library/Pages/OnePageCheckoutPage";
import BillingAddressForm, {
    BillingAddressFormStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/BillingAddressForm";
import BillToSelectorModal, {
    BillToSelectorModalStyles,
} from "@insite/content-library/Widgets/OnePageCheckout/BillToSelectorModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    cart?: Cart;
}

const mapStateToProps = (state: CustomAppState, { cart }: OwnProps) => {
    return {
        countries: getCurrentCountries(state),
        currentUserIsGuest: getCurrentUserIsGuest(state),
        billToState: getBillToState(state, cart?.billToId),
        billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        billingAddressFormState: state.pages.onePageCheckout.billingAddressFormState,
        isBillingAddressUpdateRequired: state.pages.onePageCheckout.isBillingAddressUpdateRequired,
        useBillingAddress: state.xOnePageCheckout.useBillingAddress,
        isBillingAddressTheSameAsShipping: state.pages.onePageCheckout.isBillingAddressTheSameAsShipping,
        isBillingAddressEditing: state.xOnePageCheckout.isBillingAddressEditing,
        isShippingAddressEditing: state.xOnePageCheckout.isShippingAddressEditing,
    };
};

const mapDispatchToProps = {
    setAddressBookOverlayIsOpen,
    xSaveShipping,
    setPaymentDetailsState,
    setAddress: (address: BillToModel) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/OnePageCheckout/SetBillingAddress",
            address,
        });
    },
    setIsBillingAddressEditing: (isEditing: boolean) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xOnePageCheckout/IsBillingAddressEditing" as any,
            isEditing,
        });
    },
    setIsBillingAddressUpdateRequired: (isBillingAddressUpdateRequired: boolean) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "Pages/OnePageCheckout/SetIsBillingAddressUpdateRequired",
            isBillingAddressUpdateRequired,
        });
    },
    setIsShippingAddressUpdateRequired:
        (isShippingAddressUpdateRequired: boolean) => (dispatch: Dispatch<AnyAction>) => {
            dispatch({
                type: "Pages/OnePageCheckout/SetIsShippingAddressUpdateRequired",
                isShippingAddressUpdateRequired,
            });
        },
    setIsSubmitting: (isSubmitting: boolean) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xOnePageCheckout/IsSubmitting" as any,
            isSubmitting,
        });
    },
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

interface OnePageCheckoutBillingAddressStyles {
    container?: InjectableCss;
    titleContainer?: InjectableCss;
    heading?: TypographyPresentationProps;
    editSavedAddressesLink?: LinkPresentationProps;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    billToContainer?: InjectableCss;
    billingAddressForm?: BillingAddressFormStyles;
    billToSelectorModal?: BillToSelectorModalStyles;
    editAddressLink?: LinkPresentationProps;
    actionContainer?: InjectableCss;
    saveAddressButton?: ButtonPresentationProps;
    cancelEditButton?: ButtonPresentationProps;
}

export const onePageCheckoutBillingAddressStyles: OnePageCheckoutBillingAddressStyles = {};

const styles = onePageCheckoutBillingAddressStyles;
const OnePageCheckoutBillingAddress: FC<Props> = ({
    cart,
    countries,
    currentUserIsGuest,
    billToAddressFields,
    billToState,
    theme,
    setAddressBookOverlayIsOpen,
    isBillingAddressUpdateRequired,
    xSaveShipping,
    setPaymentDetailsState,
    isBillingAddressTheSameAsShipping,
    billingAddressFormState,
    setAddress,
    isBillingAddressEditing,
    isShippingAddressEditing,
    setIsBillingAddressEditing,
    setIsBillingAddressUpdateRequired,
    setIsShippingAddressUpdateRequired,
    setIsSubmitting,
}) => {
    const context = useContext(OnePageCheckoutFormContext);
    /**
     * XNG-Changes
     * To Add "Edit Address" and handle it
     */
    const [billingAddressFormStateSource, setBillingAddressFormStateSource] = useState(billingAddressFormState);
    const [isBillToSelectorModalOpen, setIsBillToSelectorModalOpen] = useState(false);
    const billTo = billToState.value;

    const editBillingAddressClickHandler = () => {
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: true })
            : setIsBillToSelectorModalOpen(true);
    };

    const billToSelectorModalCloseHandler = () => {
        setIsBillToSelectorModalOpen(false);
    };

    /**
     * XNG-Changes
     * Saved All Addresses
     * if isBillingAddressTheSameAsShipping,
     */
    const saveClickHandler = async () => {
        setIsSubmitting(true);

        const validators: (() => Promise<boolean>)[] = [];
        Object.keys(context.validators).forEach(key => {
            const validator = context.validators[key];
            /**
             * Ignore useBillingAddress Form
             */
            if (isBillingAddressTheSameAsShipping && key === "shippingAddress") {
                return;
            }

            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        if (results.every(o => o)) {
            context.formSubmitAttempted = true;
            xSaveShipping({
                allowUpdateBillTo: true,
                onSuccess: () => {
                    context.formSubmitAttempted = false;
                    setPaymentDetailsState({ paymentDetailsState: "active" });
                    setIsBillingAddressEditing(false);
                },
            });
        } else {
            if (!isBillingAddressEditing && !isBillingAddressTheSameAsShipping) {
                setIsBillingAddressUpdateRequired(true);
                setIsShippingAddressUpdateRequired(true);
            }
            context.formSubmitAttempted = false;
            doScroll();
        }
    };

    const billingEditAddressOpen = () => {
        if (isShippingAddressEditing) {
            return;
        }

        setBillingAddressFormStateSource(billingAddressFormState);
        setIsBillingAddressEditing(true);
    };

    /**
     * XNG-Changes
     * Reset BillingAddressFormState when click on Cancel
     */
    const billingEditAddressClose = () => {
        if (!billingAddressFormStateSource?.address) {
            return;
        }

        setAddress({
            ...billingAddressFormStateSource?.address,
        });
        setIsBillingAddressEditing(false);
    };

    if (!countries || !billTo) {
        return null;
    }

    /**
     * XNG-Changes
     * Update Structure
     * Add condition for Quote
     */
    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.titleContainer}>
                <Typography {...styles.heading}>{translate("Billing Address")}</Typography>
                {billTo &&
                    cart?.type !== "Quote" &&
                    !currentUserIsGuest &&
                    cart?.status !== "QuoteProposed" &&
                    !(cart?.status === "Cart" && cart?.type === "Job") && (
                        <>
                            <Link
                                {...styles.editSavedAddressesLink}
                                onClick={editBillingAddressClickHandler}
                                data-test-selector="editBillingAddressButton"
                            >
                                {translate("Select Saved Address")}
                                <VisuallyHidden>{translate("Billing Address")}</VisuallyHidden>
                            </Link>
                            <BillToSelectorModal
                                isOpen={isBillToSelectorModalOpen}
                                onModalClose={billToSelectorModalCloseHandler}
                                extendedStyles={styles.billToSelectorModal}
                            />
                        </>
                    )}
            </StyledWrapper>
            {isBillingAddressUpdateRequired || isBillingAddressEditing ? (
                <StyledWrapper {...styles.billToContainer}>
                    {billToAddressFields && (
                        <BillingAddressForm
                            countries={countries}
                            fieldDisplay={billToAddressFields}
                            extendedStyles={styles.billingAddressForm}
                            {...{ formId: CHECKOUT_BILLING_FORM_ID }}
                        />
                    )}
                    <StyledWrapper {...styles.actionContainer}>
                        <Button onClick={saveClickHandler} {...styles.saveAddressButton} className="full-width">
                            {translate("Save Address")}
                        </Button>
                        {isBillingAddressEditing && (
                            <Button onClick={billingEditAddressClose} {...styles.cancelEditButton}>
                                {translate("Cancel")}
                            </Button>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <StyledWrapper>
                    {billTo && (
                        <AddressInfoDisplay
                            {...billTo}
                            state={billTo.state ? billTo.state.abbreviation : undefined}
                            country={billTo.country ? billTo.country.abbreviation : undefined}
                            extendedStyles={styles.addressInfoDisplay}
                        />
                    )}
                    {currentUserIsGuest && (
                        <Link
                            onClick={billingEditAddressOpen}
                            {...styles.editAddressLink}
                            disabled={isShippingAddressEditing}
                        >
                            {translate("Edit Address")}
                        </Link>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(OnePageCheckoutBillingAddress));
